import { useCallback, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import { useInAppWindowFocus } from "@/lib/inAppWindowFocus";
import { MODAL_STACK_GROUP_DOCUMENT_EDITOR, useModalStackControls } from "@/lib/modalStack";
import { AuftragsbestaetigungFromAngebotProvider } from "@/pages/dokumente/AuftragsbestaetigungFromAngebotProvider";
import {
  DocumentEditorContext,
  type DocumentEditorContextValue,
  type DocumentEditorOpenOptions,
} from "@/pages/dokumente/documentEditorContextValue";

export function DocumentEditorProvider({ children }: { children: ReactNode }) {
  const { focusSurface } = useInAppWindowFocus();
  const { bringGroupToFront } = useModalStackControls();
  const [isOpen, setIsOpen] = useState(false);
  const [docId, setDocId] = useState<number | null>(null);
  const onClosedRef = useRef<(() => void) | null>(null);
  const onDeletedRef = useRef<(() => void) | null>(null);

  const close = useCallback(() => {
    const onClosed = onClosedRef.current;
    setIsOpen(false);
    setDocId(null);
    onClosedRef.current = null;
    onDeletedRef.current = null;
    onClosed?.();
  }, []);

  const open = useCallback(
    (options: DocumentEditorOpenOptions) => {
      onClosedRef.current = options.onClosed ?? null;
      onDeletedRef.current = options.onDeleted ?? null;
      setDocId(options.docId);
      focusSurface("documentEditor");
      bringGroupToFront(MODAL_STACK_GROUP_DOCUMENT_EDITOR);
      setIsOpen(true);
    },
    [focusSurface, bringGroupToFront],
  );

  const notifyDeleted = useCallback(() => {
    onDeletedRef.current?.();
    onDeletedRef.current = null;
  }, []);

  const changeDocId = useCallback((nextDocId: number) => {
    setDocId(nextDocId);
  }, []);

  const value = useMemo<DocumentEditorContextValue>(
    () => ({ open, close, isOpen, docId, notifyDeleted, changeDocId }),
    [open, close, isOpen, docId, notifyDeleted, changeDocId],
  );

  return (
    <DocumentEditorContext.Provider value={value}>
      <AuftragsbestaetigungFromAngebotProvider renderDialog={false}>
        {children}
      </AuftragsbestaetigungFromAngebotProvider>
    </DocumentEditorContext.Provider>
  );
}
